import React, { useState, type FC, type MouseEventHandler, useEffect, useMemo, type ChangeEvent } from 'react';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconDelete from "@mui/icons-material/DeleteOutline";
import IconEdit from "@mui/icons-material/Edit";
import IconWarning from "@mui/icons-material/Report";
import { type FormikContextType, type FormikHelpers, useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import pick from 'lodash/pick';
import set from 'lodash/set';
import toString from 'lodash/toString';

import { Button, TextBox } from 'components/Shared/Inputs';
import { type IActivityDetails, type IContactAddress } from 'components/CaseManagement/interfaces';
import { Spinner } from 'components/Shared';
import { DeleteAddressModal } from '../EmergencyContactDialogs/DeleteAddressModal';
import { COUNTRY_CODE, useProviderCountryCode } from 'utils/regionUtils';
import { snackBarMessageWithSupportId } from '@/TelemetryInit';

import './EmergencyContact.scss';

function getPristineAddress(): IContactAddress {
    return {
        addressLine: "",
        provinceStateName: "",
        postalZipCode: "",
        cityName: ""
    };
}

function isPristine(values: IContactAddress) {
    const pristineAddressForm = getPristineAddress();
    return !values || Object.keys(pristineAddressForm).every(key => !values[key]);
}

type FullAddressSubmitHandler = (values: IContactAddress, formikHelpers: FormikHelpers<IContactAddress>) => void | Promise<any>

interface FullAddressFormProps {
    fullAddress: IContactAddress
    handleCancelChanges?: MouseEventHandler
    handleSubmit?: MouseEventHandler | FullAddressSubmitHandler
    setFormikContext?: (formik: FormikContextType<IContactAddress> | null) => void
    readonly?: boolean
    regionCode?: COUNTRY_CODE
}

type LabelAndRules = {
    label: string;
    rules: {
        minLength?: number;
        maxLength?: number;
        format?: RegExp;
        formatExample?: string;
    }
}

interface FieldByRegion {
    addressLine: LabelAndRules;
    cityName: LabelAndRules;
    provinceStateName: LabelAndRules;
    postalZipCode: LabelAndRules;
}

export function getLabelsAndRulesByRegion(t: TFunction<"emergencyContact">, regionCode: COUNTRY_CODE) {
    const MAX_STREET_LENGTH = 75;
    const MAX_CITY_LENGTH = 50;
    const MAX_PROVINCE_LENGTH = 45;
    const MAX_ZIPCODE_LENGTH = 8;
    
    const numbersOnly = /^\d+$/;
    const canadianPostalCode = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;
    const ukPostalCode = /^[A-Za-z0-9]+[ ]?[A-Za-z0-9]*$/; // /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))[0-9][A-Za-z]{2})$/;
    const irelandEirCode = /^[A-Za-z0-9]+[ ]?[A-Za-z0-9]*$/; // /(?:^[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$/;

    const fields: FieldByRegion = {
        addressLine: {
            label: t('street_address'),
            rules: {
                maxLength: MAX_STREET_LENGTH,
            }
        },
        cityName: {
            label: t('city_town'),
            rules: {
                maxLength: MAX_CITY_LENGTH,
            }
        },
        provinceStateName: {
            label: t('state'),
            rules: {
                maxLength: MAX_PROVINCE_LENGTH,
            }
        },
        postalZipCode: {
            label: t('zip_postal_code'),
            rules: {
                maxLength: MAX_ZIPCODE_LENGTH,
                minLength: 0,
                format: numbersOnly
            }
        }
    };

    switch(regionCode) {
        case COUNTRY_CODE.CANADA: {
            fields.provinceStateName.label = t('province');
            fields.postalZipCode.label = t('postal_code');
            fields.postalZipCode.rules.format = canadianPostalCode;
            fields.postalZipCode.rules.formatExample = '"L7A2S3" or "M2H 3B4"';
            fields.postalZipCode.rules.maxLength = 7;
            break;
        }
        case COUNTRY_CODE.NZ:
        case COUNTRY_CODE.AUSTRALIA: {
            fields.cityName.label = t('city_suburb');
            fields.postalZipCode.label = t('post_code');
            fields.postalZipCode.rules.formatExample = '"4020"';
            fields.postalZipCode.rules.maxLength = 4;
            fields.postalZipCode.rules.minLength = 4;
            break;
        }
        case COUNTRY_CODE.USA: {
            fields.postalZipCode.label = t('zip_code');
            fields.postalZipCode.rules.maxLength = 5;
            fields.postalZipCode.rules.minLength = 5;
            fields.postalZipCode.rules.formatExample = '"94130"';
            break;
        }
        case COUNTRY_CODE.GB:
        case COUNTRY_CODE.UK: {
            fields.provinceStateName.label = t('locality');
            fields.postalZipCode.label = t('postcode');
            fields.postalZipCode.rules.maxLength = 9;
            fields.postalZipCode.rules.formatExample = '"SE50EG" or "CW3 9SS" or "WC2H 7LT"';
            fields.postalZipCode.rules.format = ukPostalCode
            break;
        }
        case COUNTRY_CODE.IRE: {
            fields.provinceStateName.label = t('locality');
            fields.postalZipCode.label = t('eircode');
            fields.postalZipCode.rules.formatExample = '"C01H075" or "D04 V089"';
            fields.postalZipCode.rules.format = irelandEirCode;
            break;
        }
    }
    return fields;
}

export const FullAddressForm: FC<FullAddressFormProps> = ({ fullAddress, handleCancelChanges, handleSubmit, setFormikContext, readonly = false, regionCode }) => {
    const { t, ready: translationsReady } = useTranslation("emergencyContact", { useSuspense: false });
    const fieldsByRegion = getLabelsAndRulesByRegion(t, regionCode!);
    const isUKRegion = [COUNTRY_CODE.GB, COUNTRY_CODE.IRE].includes(regionCode!);
    const getLabelAndMaxLength = (field: LabelAndRules) => ({ label: field.label, maxLength: field.rules.maxLength });
    const getLabelAndMinLength = (field: LabelAndRules) => ({ label: field.label, minLength: field.rules.minLength });
    const getLabelAndExample = (field: LabelAndRules) => ({ label: field.label, example: field.rules.formatExample });
    
    const ContactAddressSchema = Yup.object().shape({
        addressLine: Yup.string()
                    .max(fieldsByRegion.addressLine.rules.maxLength!, t("fieldname_cannot_exceed_x_chars", { replace: getLabelAndMaxLength(fieldsByRegion.addressLine) }))
                    .required(t('fieldname_is_required', { replace: pick(fieldsByRegion.addressLine, ['label']) })),
        cityName: Yup.string()
                    .max(fieldsByRegion.cityName.rules.maxLength!, t("fieldname_cannot_exceed_x_chars", { replace: getLabelAndMaxLength(fieldsByRegion.cityName) }))
                    .required(t('fieldname_is_required', { replace: pick(fieldsByRegion.cityName, ['label']) })),
        provinceStateName: Yup.string()
                        .max(fieldsByRegion.provinceStateName.rules.maxLength!, t("fieldname_cannot_exceed_x_chars", { replace: getLabelAndMaxLength(fieldsByRegion.provinceStateName) }))
                        .when('$isUKRegion', {
                            is: true,
                            then: s => s.nullable().optional(),
                            otherwise: s => s.required(t('fieldname_is_required', { replace: pick(fieldsByRegion.provinceStateName, ['label']) }))
                        }),
        postalZipCode: Yup.string()
                        .min(fieldsByRegion.postalZipCode.rules.minLength!, t("fieldname_must_be_at_least_x_chars", { replace: getLabelAndMinLength(fieldsByRegion.postalZipCode) }))
                        .max(fieldsByRegion.postalZipCode.rules.maxLength!, t("fieldname_cannot_exceed_x_chars", { replace: getLabelAndMaxLength(fieldsByRegion.postalZipCode) }))
                        .matches(fieldsByRegion.postalZipCode.rules.format!, t(isUKRegion ? "fieldname_format_is_wrong_please_correct" : "fieldname_does_not_match_the_format_x", { replace: getLabelAndExample(fieldsByRegion.postalZipCode) }))
                        .required(t('fieldname_is_required', { replace: pick(fieldsByRegion.postalZipCode, ['label']) }))
    });

    const formik = useFormik<IContactAddress>({
        initialValues: fullAddress,
        onSubmit: handleSubmit as FullAddressSubmitHandler,
        validateOnBlur: true,
        validate: (values) => {
            try {
                ContactAddressSchema.validateSync(values, {
                    abortEarly: false,
                    context: { isUKRegion }
                });
            } catch (error) {
                if (error instanceof Error && error.name !== "ValidationError") {
                    throw error;
                }
            
                return (error as Yup.ValidationError).inner.reduce((errors, currentError) => {
                    errors = set(errors, currentError.path!, currentError.message);
                    return errors;
                }, {});
            }
        
            return {};
        }
    });

    useEffect(() => {
        // console.info("🍤👶🍖 values, errors:", formik.values, formik.errors);
        setFormikContext?.(formik);
        return () => setFormikContext?.(null);
    }, [formik.values, formik.errors, formik.isValid]);
    
    const getErrorMessage = (fieldName: string) => {
        return !!formik.errors[fieldName] ? (
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <IconWarning style={{ height: '16px' }} />{formik.errors[fieldName]}
            </span>
         ) : '';
    }

    return translationsReady ? (
        <Grid container component='form' onSubmit={formik.handleSubmit} spacing={3}>
            <Grid item xs={12} container spacing={2}>
                <Grid item xs={12}>
                    <TextBox id="addressLine" helperText={getErrorMessage('addressLine')} value={formik.values?.addressLine} label={fieldsByRegion.addressLine.label} onChange={formik.handleChange} fullWidth error={Boolean(formik.errors.addressLine)} readOnly={readonly} />
                </Grid>
                <Grid item xs={12}>
                    <TextBox id="cityName" helperText={getErrorMessage('cityName')} value={formik.values?.cityName} label={fieldsByRegion.cityName.label} onChange={formik.handleChange} fullWidth error={Boolean(formik.errors.cityName)} readOnly={readonly} />
                </Grid>
                <Grid item xs={12}>
                    <TextBox id="provinceStateName" helperText={getErrorMessage('provinceStateName')} value={formik.values?.provinceStateName} label={fieldsByRegion.provinceStateName.label} onChange={formik.handleChange} fullWidth error={Boolean(formik.errors.provinceStateName)} readOnly={readonly} />
                </Grid>
                <Grid item xs={12}>
                    <TextBox id="postalZipCode" helperText={getErrorMessage('postalZipCode')} value={formik.values?.postalZipCode} label={fieldsByRegion.postalZipCode.label} onChange={formik.handleChange} fullWidth error={Boolean(formik.errors.postalZipCode)} readOnly={readonly} />
                </Grid>
            </Grid>
            {
            (!!handleCancelChanges && !!handleSubmit) &&
            <Grid item xs={12} container spacing={2}>
                <Grid item>
                    <Button type="submit" variant="opaque" onClick={handleSubmit as MouseEventHandler}>
                        Save
                    </Button>
                </Grid>
                <Grid item>
                    <Button type="button" variant="text" onClick={handleCancelChanges}>Cancel</Button>
                </Grid>
            </Grid>
            }
        </Grid>
    ) : <Spinner fillParent />;
}

interface ClientContactFromProps {
    activity: IActivityDetails
    title?: string
    onSubmit?: (addressInfo: IContactAddress) => Promise<any>
    afterSubmit?: () => void
    onCancel?: () => void
    busy?: boolean
    viewMode?: 'tab' | 'form'
}

const onlyAddressFields = <T extends IContactAddress>(addressObject: T) =>
    pick(addressObject ?? {}, ['addressLine', 'cityName', 'provinceStateName', 'postalZipCode']);

export const ClientContactForm: FC<ClientContactFromProps> = ({ activity, onSubmit, title, afterSubmit, onCancel, busy, viewMode = 'form' }) => {
    const { t, ready: translationsReady } = useTranslation("emergencyContact", { useSuspense: false });
    const [addressFormik, setAddressFormik] = useState<FormikContextType<IContactAddress>|null>(null);
    const fullAddress = useMemo(() => onlyAddressFields(activity?.client?.contactAddress!) || getPristineAddress(), [activity?.client?.contactAddress]);
    const [addressDeclined, setAddressDeclined] = useState<boolean|null|undefined>(activity?.client?.contactAddress?.clientAddressDeclined);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const addressExists = !!fullAddress.addressLine;
    const isPrefilled = () => viewMode === 'form' && addressExists;
    const [readonly, setReadonly] = useState(isPrefilled());
    const { enqueueSnackbar } = useSnackbar();
    const regionCode = useProviderCountryCode(activity?.providerId);

    const neverFilledBefore = () => isPristine(fullAddress) && isPristine(addressFormik?.values!); 

    const handleSubmit = () => {
        if (!addressFormik?.isValid) {
            enqueueSnackbar(t('please_fix_all_errors'), { variant: "error" });
            return;
        }
        const clientAddressDeclined = isPrefilled() ? false : addressDeclined ?? false;
        onSubmit
            ?.({ ...addressFormik?.values, clientAddressDeclined })
            .then(afterSubmit)
            .then(() => enqueueSnackbar(t('client_address_updated_successfully'), { variant: "success" }))
            .catch((err) => enqueueSnackbar(err?.message || err, { variant: "error" }));
    }

    const handleCancel = () => {
        onCancel?.();
    }
    
    function handleDecline() {
        if (neverFilledBefore() && !addressDeclined) {
            setAddressDeclined(true);
            addressFormik?.setErrors({});
        } else if (!addressDeclined) {
            setShowDeleteModal(true);
        } else if (addressDeclined) {
            setAddressDeclined(false);
        }
    }

    function handleConfirm(e: ChangeEvent<HTMLInputElement>) {
        setAddressDeclined(isPrefilled() && readonly && !e.target.checked ? null : !e.target.checked);
    }

    function setEditMode() {
        setReadonly(false);
    }

    function decline() {
        setAddressDeclined(true);
        // addressFormik?.setValues(getPristineAddress());
        setShowDeleteModal(false);
        onSubmit
            ?.({ ...addressFormik?.values!, clientAddressDeclined: true })
            .then(afterSubmit)
            .then(() => enqueueSnackbar(t('client_address_removed_successfully'), { variant: "success" }))
            .catch((err) => enqueueSnackbar(snackBarMessageWithSupportId(err?.message) || err, { variant: "error" }));
    }
 
    const containsChanges = useMemo(() => {
        const originalAddress = onlyAddressFields(activity?.client?.contactAddress!);
        return Object.keys(addressFormik?.values ?? {}).some(key =>  toString(originalAddress?.[key]) !== toString(addressFormik?.values[key]));
    }, [fullAddress, addressFormik?.values]);
    
    function getCompleteButtonStatus() {
        if (!isPrefilled()) {
            return (!addressFormik?.isValid || isPristine(addressFormik?.values!)) && !addressDeclined
        } else {
            // (!addressFormik?.isValid || isPristine(addressFormik?.values!)) || addressDeclined
            if (!readonly && !addressFormik?.isValid) {
                return true;
            }
            if (addressDeclined === null && !containsChanges) {
                return true;
            }
            if (addressDeclined === false || containsChanges) {
                return false;
            }
        }
    }

    return translationsReady ? (
        <Grid container>
            {title && (
                <Grid item xs={12}>
                    <Grid container>
                        <Grid item md={10}><h2>{title}</h2></Grid>
                        {isPrefilled() && (
                            <Grid container md={2}>
                                <Grid item xs={9} style={{ textAlign: 'right' }}><Button variant="text" round  onClick={handleDecline}><IconDelete color="error" /></Button></Grid>
                                <Grid item xs={3} style={{ textAlign: 'right' }}><Button variant="text" disabled={!readonly} round onClick={setEditMode} ><IconEdit color={readonly ? "primary" : "disabled" } /></Button></Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            )}
            <FullAddressForm readonly={(!isPrefilled() && addressDeclined) || readonly} fullAddress={fullAddress} setFormikContext={setAddressFormik} handleSubmit={handleSubmit} regionCode={regionCode} />

            <Divider style={{ width: "100%", margin: "15px 0" }} />

            <Grid item xs={12}>
                {isPrefilled()
                ? (<FormControlLabel label={t('client_address_info_is_up_to_date')} control={
                    <Checkbox
                        checked={addressDeclined===false && !containsChanges}
                        onChange={handleConfirm}
                        name="clientAddressConfirmed"
                        color="primary"
                        disabled={containsChanges}
                    />
                    }
                />)
                :(<FormControlLabel label={t('client_did_not_provide_full_address')} control={
                    <Checkbox
                        checked={addressDeclined===true}
                        onChange={handleDecline}
                        name="clientAddressDeclined"
                        color="primary"
                    />
                    }
                />)}
            </Grid>
            <Grid style={{ marginTop: "16px" }} container spacing={2}>
                <Grid item alignContent={'center'}>
                    {isPrefilled()
                       ? (
                            <Button type="submit" variant="opaque" disabled={getCompleteButtonStatus()} onClick={handleSubmit} busy={busy}>
                                {t('complete')}
                            </Button>
                        )
                       : (
                            <Button type="submit" variant="opaque" disabled={getCompleteButtonStatus()} onClick={handleSubmit} busy={busy}>
                                {t('complete')}
                            </Button>
                        )}
                </Grid>
                {(!!onCancel || (isPrefilled() && !readonly)) && (
                    <Grid item alignContent={'center'}>
                        <Button type="submit" variant="outline" onClick={handleCancel}>
                            {t('cancel')}
                        </Button>
                    </Grid>
                )}
            </Grid>
            <DeleteAddressModal
                open={showDeleteModal}
                btnAction={decline}
                onClose={() => { setShowDeleteModal(false); if (addressDeclined) setAddressDeclined(false) }}
            />
        </Grid>
    ) : <Spinner fillParent />;
}
